export type BlockSubType = 'default' | 'specific' | 'zero';

export type BlockRole = 'header' | 'footer';

/**
 * The subtype of a block, whether or not it went through the migration.
 *
 * `entitySubType` is produced by the migration. A block whose migration failed is
 * handed back raw and states its kind the persisted way instead — `type` of
 * `'DEFAULT'`, `'specific'` or `'zero'` — so routing on `entitySubType` alone
 * sends every raw block down the default-block path, or off the page entirely.
 *
 * The oldest blocks predate the tag on both sides and answer `undefined`; they
 * were never rendered before either.
 */
export const blockSubType = (block: unknown): BlockSubType | undefined => {
    const bag = (block ?? {}) as { entitySubType?: unknown; type?: unknown };

    if (bag.entitySubType === 'default' || bag.entitySubType === 'specific' || bag.entitySubType === 'zero') {
        return bag.entitySubType;
    }

    switch (bag.type) {
        case 'DEFAULT':
            return 'default';
        case 'specific':
            return 'specific';
        case 'zero':
            return 'zero';
        default:
            return undefined;
    }
};

/**
 * Whether the block is one of the site-wide header/footer blocks — read from
 * `meta` after the migration, from the persisted flag before it. See
 * {@link blockSubType} for why both spellings have to be handled.
 */
export const blockIsShared = (block: unknown): boolean => {
    const bag = (block ?? {}) as { meta?: { isShared?: unknown }; isShared?: unknown };

    return !!(bag.meta ? bag.meta.isShared : bag.isShared);
};

type SharingBag = {
    meta?: { role?: unknown; hiddenOnPages?: unknown };
    sharedBlockProps?: { type?: unknown; hideOnPage?: Record<string, unknown> };
};

const asRole = (value: unknown): BlockRole | undefined =>
    value === 'header' || value === 'footer' ? value : undefined;

/**
 * Header or footer — on the shared block and on its copies alike. Reads both
 * spellings for the reason {@link blockIsShared} does.
 */
export const blockRole = (block: unknown): BlockRole | undefined => {
    const bag = (block ?? {}) as SharingBag;

    return asRole(bag.meta ? bag.meta.role : bag.sharedBlockProps?.type);
};

/**
 * The meta a copy of a shared block carries: still a header or a footer, but no
 * longer the site-wide instance, so it loses the flag and the hidden pages.
 */
export const toUnsharedBlockMeta = <T extends { isShared?: true; hiddenOnPages?: string[] }>(
    meta: T
): Omit<T, 'isShared' | 'hiddenOnPages'> => {
    const copy = { ...meta };

    delete copy.isShared;
    delete copy.hiddenOnPages;

    return copy;
};

/** Whether the shared block is switched off on the given page. */
export const isBlockHiddenOnPage = (block: unknown, pageId: string | number | null | undefined): boolean => {
    if (pageId == null) {
        return false;
    }

    const bag = (block ?? {}) as SharingBag;

    if (bag.meta) {
        const hidden = bag.meta.hiddenOnPages;

        return Array.isArray(hidden) && hidden.includes(String(pageId));
    }

    return !!bag.sharedBlockProps?.hideOnPage?.[String(pageId)];
};

/** The member of a block union that carries the given subtype. */
type BlockOfSubType<T, S extends BlockSubType> = Extract<T, { entitySubType: S }>;

/**
 * The subtype guards. They answer through {@link blockSubType} rather than
 * reading `entitySubType` directly, so a block whose migration failed routes to
 * the same component it would have before — and they narrow, so the branch that
 * renders a canvas holds the canvas type instead of the whole union.
 *
 * Generic over the union rather than typed on `BlockEntity`: a host hands in its
 * own denormalized shapes — children as entities rather than ids — and `Extract`
 * picks the member of whichever union it is given.
 */
export const isDefaultBlock = <T>(block: T): block is BlockOfSubType<T, 'default'> => blockSubType(block) === 'default';

export const isSpecificBlock = <T>(block: T): block is BlockOfSubType<T, 'specific'> =>
    blockSubType(block) === 'specific';

export const isZeroBlock = <T>(block: T): block is BlockOfSubType<T, 'zero'> => blockSubType(block) === 'zero';

/**
 * The one site-wide header/footer instance. It is an ordinary block by subtype —
 * only `meta.isShared` tells it apart — hence the narrowing to 'default'.
 */
export const isSharedBlock = <T>(block: T): block is BlockOfSubType<T, 'default'> =>
    isDefaultBlock(block) && blockIsShared(block);
